import { useEffect } from "react";

/**
 * Excel-style keyboard navigation for data-entry grids.
 *
 *   Enter / ArrowDown  → move focus DOWN to the next visible input in the
 *                        same column (closest center-X match below).
 *   ArrowUp            → move focus UP to the closest input above.
 *   ArrowRight         → move focus RIGHT to the closest input in the same
 *                        row (similar center-Y) — but only when the text
 *                        cursor is at the END of the current value
 *                        (otherwise the browser's own right-arrow text
 *                        cursor movement is preserved).
 *   ArrowLeft          → move focus LEFT, same rules as right but firing
 *                        only when the cursor is at the START of the value.
 *
 * Skipped (the hook is permissive — leaves native behavior alone):
 *   - inputs inside a <form>          (login / password-reset submit)
 *   - inputs of type password / email / search / submit / button / file /
 *     checkbox / radio / hidden / date / time / datetime-local /
 *     month / week / color / range / image / reset
 *   - inputs inside any open Radix popover / select / combobox / dialog
 *   - readOnly / disabled inputs as targets
 *   - inputs explicitly opted out via data-skip-enter-nav="true"
 *   - Shift / Ctrl / Alt / Meta + arrow (let native selection / browser
 *     shortcuts pass through)
 *   - IME composition events (Asian languages)
 *
 * Install once in Layout.jsx so it runs only in the logged-in shell.
 */
const SKIP_TYPES = new Set([
  "password", "email", "search", "submit", "button", "file",
  "checkbox", "radio", "hidden", "date", "time", "datetime-local",
  "month", "week", "color", "range", "image", "reset",
]);

const SKIP_ANCESTOR_SELECTOR = [
  "form",
  "[role='dialog']",
  "[role='combobox']",
  "[role='listbox']",
  "[role='menu']",
  "[data-radix-popper-content-wrapper]",
  '[data-state="open"][data-side]',
].join(",");

const COLUMN_TOLERANCE_PX = 14;
const ROW_TOLERANCE_PX = 14;

function shouldSkipInput(el) {
  if (!el || el.tagName !== "INPUT") return true;
  if (el.disabled || el.readOnly) return true;
  if (el.dataset?.skipEnterNav === "true") return true;
  const t = (el.type || "text").toLowerCase();
  if (SKIP_TYPES.has(t)) return true;
  if (el.closest(SKIP_ANCESTOR_SELECTOR)) return true;
  return false;
}

function isFocusableInput(el) {
  if (!el || el.tagName !== "INPUT") return false;
  if (el.disabled || el.readOnly) return false;
  const t = (el.type || "text").toLowerCase();
  if (SKIP_TYPES.has(t)) return false;
  if (el.offsetParent === null && el.getClientRects().length === 0) return false;
  return true;
}

/**
 * Find the best candidate input given a direction.
 *   dir: "down" | "up" | "right" | "left"
 *   refRect: the source input's bounding rect (center used).
 *   inputs: array of candidate inputs (current source already excluded).
 */
function findNeighbor(dir, refRect, inputs) {
  const cx = refRect.left + refRect.width / 2;
  const cy = refRect.top + refRect.height / 2;

  const candidates = inputs
    .map((i) => {
      const r = i.getBoundingClientRect();
      return { el: i, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
    });

  let filtered;
  if (dir === "down" || dir === "up") {
    // Same column (X close), DIFFERENT row in the requested direction
    filtered = candidates.filter((c) => Math.abs(c.cx - cx) <= COLUMN_TOLERANCE_PX);
    filtered = filtered.filter((c) => (dir === "down" ? c.cy > cy + 2 : c.cy < cy - 2));
    filtered.sort((a, b) => {
      const dy = dir === "down" ? a.cy - b.cy : b.cy - a.cy;
      if (dy !== 0) return dy;
      return Math.abs(a.cx - cx) - Math.abs(b.cx - cx);
    });
  } else {
    // Same row (Y close), DIFFERENT column in the requested direction
    filtered = candidates.filter((c) => Math.abs(c.cy - cy) <= ROW_TOLERANCE_PX);
    filtered = filtered.filter((c) => (dir === "right" ? c.cx > cx + 2 : c.cx < cx - 2));
    filtered.sort((a, b) => {
      const dx = dir === "right" ? a.cx - b.cx : b.cx - a.cx;
      if (dx !== 0) return dx;
      return Math.abs(a.cy - cy) - Math.abs(b.cy - cy);
    });
  }
  return filtered.length > 0 ? filtered[0].el : null;
}

function cursorAtStart(el) {
  try {
    return el.selectionStart === 0 && el.selectionEnd === 0;
  } catch {
    // Some input types throw on selectionStart access; treat as "at edge"
    return true;
  }
}

function cursorAtEnd(el) {
  try {
    const len = (el.value || "").length;
    return el.selectionStart === len && el.selectionEnd === len;
  } catch {
    return true;
  }
}

export function useGridEnterNavigation() {
  useEffect(() => {
    const handler = (e) => {
      const key = e.key;
      if (key !== "Enter" && key !== "ArrowDown" && key !== "ArrowUp" && key !== "ArrowLeft" && key !== "ArrowRight") return;
      if (e.isComposing || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
      const el = e.target;
      if (shouldSkipInput(el)) return;

      // For left/right we only navigate when the cursor is at the appropriate
      // edge — otherwise we let the browser move the text cursor inside the
      // input value as usual.
      if (key === "ArrowLeft" && !cursorAtStart(el)) return;
      if (key === "ArrowRight" && !cursorAtEnd(el)) return;

      const all = Array.from(document.querySelectorAll("input"));
      const visible = all.filter(isFocusableInput);
      if (visible.length === 0) return;

      const others = visible.filter((i) => i !== el);
      const rect = el.getBoundingClientRect();

      const dir =
        key === "Enter" || key === "ArrowDown" ? "down"
        : key === "ArrowUp" ? "up"
        : key === "ArrowRight" ? "right"
        : "left";

      let next = findNeighbor(dir, rect, others);

      // Fallback for Enter only: jump to next DOM-order input if no column
      // match. Arrow keys (including ArrowDown) do NOT fall back — staying
      // put is safer than darting across the page.
      if (!next && key === "Enter") {
        const idx = visible.indexOf(el);
        if (idx >= 0 && idx + 1 < visible.length) next = visible[idx + 1];
      }

      if (!next) return; // nothing useful to do — let default happen

      e.preventDefault();
      next.focus();
      try {
        // For Enter/Down/Up, select the value so it can be overwritten.
        // For Left/Right, place the caret at the natural edge so continued
        // arrow presses keep walking the row.
        if (key === "Enter" || key === "ArrowDown" || key === "ArrowUp") {
          next.select();
        } else if (key === "ArrowRight") {
          // Entering from the left edge, caret at start so continued
          // right-arrow presses keep walking the row.
          next.setSelectionRange(0, 0);
        } else if (key === "ArrowLeft") {
          const len = (next.value || "").length;
          next.setSelectionRange(len, len); // entering from right, caret at end
        }
      } catch {
        /* setSelectionRange not supported on this input type — ignore */
      }
    };

    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, []);
}

export default useGridEnterNavigation;
